import { useEffect, useState } from 'react';

import { type ConsentState, DENY_ALL, EVENT_OPEN_SETTINGS, GRANT_ALL, getConsent, saveConsent } from '@/Lib/consent';
import t from '@/i18n/cookieConsent.json';
import { versioned } from '@/Lib/assetVersion';

const LOGO = versioned('/images/brand/tuitjer-logo-doc.png');

export default function CookieConsent() {
    const [mounted, setMounted] = useState(false);
    const [bannerVisible, setBannerVisible] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [draft, setDraft] = useState<ConsentState>(DENY_ALL);

    // Client-only: decide initial visibility after mount (avoids SSR mismatch).
    useEffect(() => {
        setMounted(true);
        setBannerVisible(getConsent() === null);
    }, []);

    // Allow re-opening from anywhere (e.g. footer link).
    useEffect(() => {
        const open = () => {
            setDraft(getConsent() ?? DENY_ALL);
            setModalOpen(true);
        };
        window.addEventListener(EVENT_OPEN_SETTINGS, open);
        return () => window.removeEventListener(EVENT_OPEN_SETTINGS, open);
    }, []);

    // Lock scroll + Esc-to-close while the modal is open.
    useEffect(() => {
        if (!modalOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setModalOpen(false);
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener('keydown', onKey);
        };
    }, [modalOpen]);

    if (!mounted) return null;

    const persist = (state: ConsentState) => {
        saveConsent(state);
        setBannerVisible(false);
        setModalOpen(false);
    };

    const openSettings = () => {
        setDraft(getConsent() ?? DENY_ALL);
        setModalOpen(true);
    };

    if (!bannerVisible && !modalOpen) return null;

    return (
        <>
            {/* ── Banner ─────────────────────────────────────────────── */}
            {bannerVisible && !modalOpen ? (
                <div
                    className="fixed inset-x-0 bottom-0 z-[120] border-t-2 border-[#c9923a] bg-[#fdfaf6] shadow-[0_-8px_40px_rgba(13,21,32,0.14)]"
                    role="dialog"
                    aria-label={t.banner.title}
                >
                    <div className="mx-auto flex max-w-[1440px] flex-col gap-5 px-6 py-5 sm:px-12 lg:flex-row lg:items-center lg:justify-between lg:gap-10 lg:px-[120px] lg:py-6">
                        <div className="flex max-w-[540px] flex-col gap-2">
                            <div className="flex items-center gap-3">
                                <img src={LOGO} alt="" aria-hidden="true" className="h-7 w-7 object-contain" />
                                <p className="text-[0.9375rem] font-bold text-[#0d1520]">{t.banner.title}</p>
                            </div>
                            <p className="text-[0.8125rem] leading-[1.55] text-[#5c4f42]">{t.banner.text}</p>
                            <a
                                href="/datenschutz"
                                className="text-[0.6875rem] font-medium text-gold-dark uppercase transition-colors hover:text-[#5c4a08]"
                            >
                                {t.banner.policyLink} →
                            </a>
                        </div>
                        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center lg:shrink-0">
                            <button
                                type="button"
                                onClick={() => persist(DENY_ALL)}
                                className="inline-flex h-11 items-center justify-center border border-[#c9b99a] px-5 text-[0.8125rem] font-medium text-[#6b5d50] transition-colors hover:bg-[#f0e8da]"
                            >
                                {t.banner.necessaryOnly}
                            </button>
                            <button
                                type="button"
                                onClick={openSettings}
                                className="inline-flex h-11 items-center justify-center border border-[#0d1520] px-5 text-[0.8125rem] font-medium text-[#0d1520] transition-colors hover:bg-[#0d1520] hover:text-[#f5efe4]"
                            >
                                {t.banner.settings}
                            </button>
                            <button
                                type="button"
                                onClick={() => persist(GRANT_ALL)}
                                className="inline-flex h-11 items-center justify-center bg-[#c9923a] px-6 text-[0.8125rem] font-bold text-[#0d1520] transition-colors hover:bg-[#d8a24c]"
                            >
                                {t.banner.acceptAll}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {/* ── Settings modal ─────────────────────────────────────── */}
            {modalOpen ? (
                <div className="fixed inset-0 z-[130] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-[#0d1520]/75" onClick={() => setModalOpen(false)} aria-hidden="true" />
                    <div
                        className="relative flex max-h-[90vh] w-full max-w-[560px] flex-col overflow-hidden bg-[#fdfaf6] shadow-[0_24px_32px_rgba(0,0,0,0.4)]"
                        role="dialog"
                        aria-modal="true"
                        aria-label={t.modal.title}
                    >
                        {/* Header */}
                        <div className="px-8 pt-8 pb-5">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2.5">
                                    <img src={LOGO} alt="" aria-hidden="true" className="h-7 w-7 object-contain" />
                                    <p className="font-display text-[1.25rem] text-[#0d1520]">{t.modal.title}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(false)}
                                    aria-label={t.modal.close}
                                    className="flex size-8 items-center justify-center rounded-full border border-[#ddd5c8] text-[#7a5a0a] transition-colors hover:bg-[#f0e8da]"
                                >
                                    ✕
                                </button>
                            </div>
                            <p className="mt-3 text-[0.8125rem] leading-[1.6] text-[#5c4f42]">{t.modal.description}</p>
                            <span className="mt-4 block h-0.5 w-[100px] bg-[#c9923a]" aria-hidden="true" />
                        </div>

                        {/* Category list */}
                        <div className="flex-1 overflow-y-auto px-8">
                            <CategoryRow
                                name={t.categories.necessary.name}
                                text={t.categories.necessary.text}
                                tag={t.modal.alwaysActive}
                                checked
                                disabled
                            />
                            <CategoryRow
                                name={t.categories.functional.name}
                                text={t.categories.functional.text}
                                tag={t.modal.optional}
                                checked={draft.functional}
                                onChange={(v) => setDraft((d) => ({ ...d, functional: v }))}
                            />
                            <CategoryRow
                                name={t.categories.statistics.name}
                                text={t.categories.statistics.text}
                                tag={t.modal.optional}
                                checked={draft.statistics}
                                onChange={(v) => setDraft((d) => ({ ...d, statistics: v }))}
                            />
                            <CategoryRow
                                name={t.categories.marketing.name}
                                text={t.categories.marketing.text}
                                tag={t.modal.optional}
                                checked={draft.marketing}
                                onChange={(v) => setDraft((d) => ({ ...d, marketing: v }))}
                                last
                            />
                        </div>

                        {/* Footer */}
                        <div className="flex flex-col items-center gap-4 border-t border-[#e8e0d8] bg-[#f5efe4] px-8 py-6">
                            <div className="flex flex-wrap justify-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => persist(DENY_ALL)}
                                    className="inline-flex h-10 items-center justify-center border border-[#c9b99a] px-4 text-[0.75rem] text-[#6b5d50] transition-colors hover:bg-[#f0e8da]"
                                >
                                    {t.modal.rejectAll}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => persist(draft)}
                                    className="inline-flex h-10 items-center justify-center border border-[#0d1520] px-4 text-[0.75rem] font-bold text-[#0d1520] transition-colors hover:bg-[#0d1520] hover:text-[#f5efe4]"
                                >
                                    {t.modal.save}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => persist(GRANT_ALL)}
                                    className="inline-flex h-10 items-center justify-center bg-[#c9923a] px-5 text-[0.75rem] font-bold text-[#0d1520] transition-colors hover:bg-[#d8a24c]"
                                >
                                    {t.modal.acceptAll}
                                </button>
                            </div>
                            <p className="flex items-center gap-1 text-[0.625rem] text-[#9a8e82]">
                                {t.modal.policyPre}{' '}
                                <a href="/datenschutz" className="text-gold-dark underline">
                                    {t.modal.policyLink}
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
}

function CategoryRow({
    name,
    text,
    tag,
    checked,
    disabled = false,
    last = false,
    onChange,
}: {
    name: string;
    text: string;
    tag: string;
    checked: boolean;
    disabled?: boolean;
    last?: boolean;
    onChange?: (value: boolean) => void;
}) {
    return (
        <div className={`flex items-start justify-between gap-4 py-5 ${last ? '' : 'border-b border-[#e8e0d8]'}`}>
            <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                    <p className="font-display text-[0.9375rem] font-bold text-[#0d1520]">{name}</p>
                    <span className="rounded-sm bg-[#f0e8da] px-2 py-0.5 text-[0.5625rem] font-semibold text-[#7a5a0a] uppercase">{tag}</span>
                </div>
                <p className="text-[0.75rem] leading-[1.5] text-[#6b5d50]">{text}</p>
            </div>
            <Toggle checked={checked} disabled={disabled} onChange={onChange} label={name} />
        </div>
    );
}

function Toggle({
    checked,
    disabled = false,
    onChange,
    label,
}: {
    checked: boolean;
    disabled?: boolean;
    onChange?: (v: boolean) => void;
    label: string;
}) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={label}
            disabled={disabled}
            onClick={() => !disabled && onChange?.(!checked)}
            className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? 'bg-[#c9923a]' : 'bg-[#d6ccbf]'} ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
        >
            <span className={`absolute top-0.5 size-5 rounded-full bg-white shadow transition-all ${checked ? 'left-[22px]' : 'left-0.5'}`} />
        </button>
    );
}
